/**
 * /set-password — where an invite email lands (SAD-953). Public: the signed,
 * single-use token in the link's fragment is the credential. The fragment
 * never leaves the browser, so the token stays out of access logs; it is
 * read once and only sent in the completion request's body.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Label, SpecraButton, SpecraInput, SpecraWordmark } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { InviteError, useAuth } from '../contexts/authContext';
const MIN_PASSWORD_LENGTH = 8;
/** The token from `#token=…`; empty when the link was mangled. */ export function inviteTokenFromHash(hash) {
    return new URLSearchParams(hash.replace(/^#/, '')).get('token') ?? '';
}
export default function SetPasswordPage() {
    const { completeInvite } = useAuth();
    const location = useLocation();
    const token = useMemo(()=>inviteTokenFromHash(location.hash), [
        location.hash
    ]);
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [linkInvalid, setLinkInvalid] = useState(token === '');
    const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
    const mismatch = confirm.length > 0 && confirm !== password;
    const canSubmit = password.length >= MIN_PASSWORD_LENGTH && confirm === password && !submitting;
    const handleSubmit = async (e)=>{
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        setError(null);
        try {
            await completeInvite(token, password);
        } catch (err) {
            if (err instanceof InviteError && err.code === 'invite_invalid') {
                setLinkInvalid(true);
            } else {
                setError(err instanceof Error ? err.message : 'Could not set your password');
            }
            setSubmitting(false);
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Helmet, {
                children: /*#__PURE__*/ _jsx("title", {
                    children: "Set your password - Specra"
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex h-dvh w-screen items-start justify-center bg-background pt-12 md:items-center md:pt-0",
                "data-testid": Testids.setPassword.page,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex w-full max-w-md flex-col gap-8",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center gap-2 px-4 text-center sm:px-16",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraWordmark, {
                                    height: 28,
                                    className: "mb-4"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "text-xl font-semibold",
                                    children: "Set your password"
                                }),
                                !linkInvalid && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-muted-foreground",
                                    children: "Choose a password to finish setting up your account."
                                })
                            ]
                        }),
                        linkInvalid ? /*#__PURE__*/ _jsxs("div", {
                            className: "mx-4 rounded-md border px-4 py-3 text-sm sm:mx-16",
                            "data-testid": Testids.setPassword.invalidState,
                            role: "alert",
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "font-medium",
                                    children: "This link has expired or has already been used."
                                }),
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "mt-1 text-muted-foreground",
                                    children: [
                                        "Ask your admin to resend your invite. If you already set your password,",
                                        ' ',
                                        /*#__PURE__*/ _jsx("a", {
                                            className: "font-medium underline",
                                            href: "/login",
                                            children: "sign in"
                                        }),
                                        "."
                                    ]
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs("form", {
                            onSubmit: handleSubmit,
                            className: "flex flex-col gap-4 px-4 sm:px-16",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "new-password",
                                            children: "New password"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraInput, {
                                            id: "new-password",
                                            "data-testid": Testids.setPassword.passwordInput,
                                            type: "password",
                                            autoComplete: "new-password",
                                            value: password,
                                            onChange: (e)=>setPassword(e.target.value),
                                            autoFocus: true,
                                            required: true
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: `text-xs ${tooShort ? 'text-destructive' : 'text-muted-foreground'}`,
                                            children: [
                                                "At least ",
                                                MIN_PASSWORD_LENGTH,
                                                " characters."
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-col gap-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Label, {
                                            htmlFor: "confirm-password",
                                            children: "Confirm password"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraInput, {
                                            id: "confirm-password",
                                            "data-testid": Testids.setPassword.confirmInput,
                                            type: "password",
                                            autoComplete: "new-password",
                                            value: confirm,
                                            onChange: (e)=>setConfirm(e.target.value),
                                            required: true
                                        }),
                                        mismatch && /*#__PURE__*/ _jsx("p", {
                                            className: "text-xs text-destructive",
                                            children: "Passwords do not match."
                                        })
                                    ]
                                }),
                                error && /*#__PURE__*/ _jsx("p", {
                                    className: "text-sm text-destructive",
                                    role: "alert",
                                    children: error
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "submit",
                                    "data-testid": Testids.setPassword.submit,
                                    disabled: !canSubmit,
                                    isLoading: submitting,
                                    className: "w-full",
                                    children: "Set password and sign in"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
